import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Factory,
  ChevronDown,
  Search,
  Package,
  CheckCircle,
  XCircle,
  Loader2,
  Play,
  Eye,
  MapPin,
  Hash,
  AlertTriangle,
} from 'lucide-react';
import { productionApi } from '@/api/productionApi';
import { locationsApi } from '@/api/locationsApi';
import { Button } from '@/components/common/Button';
import { ToastContainer } from '@/components/common/Toast';
import { useToast } from '@/hooks/useToast';
import { PRODUCT_TYPE_META } from '@/types/api';
import { EntityLink } from '@/components/detail/EntityLink';
import type { ProductVariant, Product, ProductionResult } from '@/types/api';

interface FlatVariantRow {
  product: Product;
  variant: ProductVariant;
}

interface ProduceModalState {
  isOpen: boolean;
  variant: ProductVariant | null;
  product: Product | null;
}

function ProduceModal({
  state,
  onClose,
  onSuccess,
}: {
  state: ProduceModalState;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [quantity, setQuantity] = useState(1);
  const [locationId, setLocationId] = useState('');
  const [preview, setPreview] = useState<ProductionResult | null>(null);
  const [result, setResult] = useState<ProductionResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.getAll,
  });

  const produceMutation = useMutation({
    mutationFn: productionApi.produceVariant,
    onSuccess: (data) => {
      setResult(data);
      onSuccess();
    },
  });

  const handlePreview = async () => {
    if (!state.variant) return;
    setPreviewLoading(true);
    try {
      const data = await productionApi.previewProduction(state.variant.id, quantity);
      setPreview(data);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleProduce = () => {
    if (!state.variant || !locationId) return;
    produceMutation.mutate({
      variantId: state.variant.id,
      quantity,
      locationId,
    });
  };

  const handleClose = () => {
    setQuantity(1);
    setLocationId('');
    setPreview(null);
    setResult(null);
    onClose();
  };

  if (!state.isOpen || !state.variant || !state.product) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 w-full max-w-lg shadow-2xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-charcoal-700">
          <div>
            <h2 className="font-sans text-xl font-bold text-gray-900 dark:text-warm-cream">Log Production Run</h2>
            <p className="text-gray-600 dark:text-warm-sand text-sm mt-0.5">
              {state.product.name} — {state.variant.displayName}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg transition-colors text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:text-warm-cream"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {result ? (
            /* Production Success View */
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-warm-cream">Production Logged!</p>
                  <p className="text-sm text-gray-600 dark:text-warm-sand">
                    {result.quantityProduced} units added to {result.locationName}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-warm-sand mb-2">Supplies Deducted</p>
                <div className="space-y-2">
                  {result.filamentDeductions.map((d) => (
                    <div
                      key={d.filamentId}
                      className="flex items-center justify-between p-3 bg-gray-100 dark:bg-charcoal-700/50 rounded-lg"
                    >
                      <span className="text-gray-900 dark:text-warm-cream text-sm">{d.filamentName}</span>
                      <span className="text-amber-450 text-sm font-medium mono">
                        -{d.gramsNeeded.toFixed(1)} g
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {result.totalCost > 0 && (
                <div className="flex items-center justify-between p-3 bg-gray-100 dark:bg-charcoal-700/50 rounded-lg">
                  <span className="text-gray-600 dark:text-warm-sand text-sm">Estimated Cost</span>
                  <span className="text-gray-900 dark:text-warm-cream font-medium">
                    ${result.totalCost.toFixed(2)}
                  </span>
                </div>
              )}

              <Button variant="secondary" className="w-full" onClick={handleClose}>
                Done
              </Button>
            </div>
          ) : (
            /* Production Form */
            <>
              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                  Quantity to Produce
                </label>
                <input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(Math.max(1, parseInt(e.target.value) || 1));
                    setPreview(null);
                  }}
                  className="input w-full"
                />
                {state.variant.unitsMakeable > 0 && (
                  <p className="text-xs text-gray-600 dark:text-warm-sand mt-1">
                    Max makeable:{' '}
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      {state.variant.unitsMakeable}
                    </span>
                  </p>
                )}
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-warm-sand mb-1.5">
                  Add to Location
                </label>
                <div className="relative">
                  <select
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    className="input w-full appearance-none pr-10"
                  >
                    <option value="">Select a location…</option>
                    {locations?.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 dark:text-warm-sand pointer-events-none" />
                </div>
              </div>

              {/* Preview Section */}
              {preview && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-warm-sand">Supply Preview</p>
                  {preview.filamentDeductions.length === 0 ? (
                    <p className="text-sm text-gray-600 dark:text-warm-sand italic">No supplies required.</p>
                  ) : (
                    preview.filamentDeductions.map((d) => (
                      <div
                        key={d.filamentId}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          d.sufficient
                            ? 'bg-gray-100 dark:bg-charcoal-700/50 border-gray-300 dark:border-charcoal-600'
                            : 'bg-red-500/10 border-red-500/30'
                        }`}
                      >
                        <div>
                          <p className="text-gray-900 dark:text-warm-cream text-sm">{d.filamentName}</p>
                          <p className="text-xs text-gray-600 dark:text-warm-sand">
                            Available: {d.gramsAvailable.toFixed(1)} g
                          </p>
                        </div>
                        <div className="text-right">
                          <p
                            className={`text-sm font-medium mono ${
                              d.sufficient ? 'text-amber-450' : 'text-red-600 dark:text-red-400'
                            }`}
                          >
                            -{d.gramsNeeded.toFixed(1)} g
                          </p>
                          {!d.sufficient && (
                            <p className="text-xs text-red-600 dark:text-red-400">Insufficient</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  {preview.totalCost > 0 && (
                    <div className="flex justify-between text-sm pt-1">
                      <span className="text-gray-600 dark:text-warm-sand">Estimated cost</span>
                      <span className="text-gray-900 dark:text-warm-cream font-medium">
                        ${preview.totalCost.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePreview}
                  isLoading={previewLoading}
                  disabled={previewLoading}
                  className="flex items-center gap-2"
                >
                  {!previewLoading && <Eye className="w-4 h-4" />}
                  Preview
                </Button>

                <Button
                  variant="primary"
                  className="flex-1 flex items-center justify-center gap-2"
                  onClick={handleProduce}
                  isLoading={produceMutation.isPending}
                  disabled={!locationId || quantity < 1 || produceMutation.isPending}
                >
                  {!produceMutation.isPending && <Play className="w-4 h-4" />}
                  Confirm Produce
                </Button>
              </div>

              {produceMutation.isError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-600 dark:text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {(produceMutation.error as Error)?.message || 'Production failed. Please try again.'}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function Production() {
  const { toasts, removeToast, success } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [modal, setModal] = useState<ProduceModalState>({
    isOpen: false,
    variant: null,
    product: null,
  });

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['production-catalog'],
    queryFn: productionApi.getCatalog,
  });

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.getAll,
  });

  // Flatten all product → variant rows
  const allRows = useMemo<FlatVariantRow[]>(() => {
    if (!products) return [];
    return products.flatMap((product) =>
      (product.variants ?? [])
        .filter((v) => v.isActive)
        .map((variant) => ({ product, variant }))
    );
  }, [products]);

  // Apply filters
  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      const matchesSearch =
        !searchQuery ||
        row.product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.variant.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.variant.variantSku.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesLocation =
        !locationFilter ||
        row.variant.shelfInventories?.some((si) => si.locationId === locationFilter);

      return matchesSearch && matchesLocation;
    });
  }, [allRows, searchQuery, locationFilter]);

  // Stats
  const stats = useMemo(() => {
    const total = allRows.length;
    const sufficient = allRows.filter((r) => r.variant.hasSufficientStock).length;
    const insufficient = total - sufficient;
    return { total, sufficient, insufficient };
  }, [allRows]);

  const openModal = (product: Product, variant: ProductVariant) => {
    setModal({ isOpen: true, product, variant });
  };

  const handleProduceSuccess = () => {
    success('Production Logged', 'Units have been added to inventory and supplies deducted.');
  };

  const typeBadgeColors: Record<string, string> = {
    THREE_D_PRINTED: 'bg-purple-500/10 text-purple-300 border border-purple-500/20',
    VINYL_CUTTING: 'bg-pink-500/10 text-pink-300 border border-pink-500/20',
    LASER_ENGRAVED: 'bg-orange-500/10 text-orange-300 border border-orange-500/20',
    STANDARD: 'bg-blue-500/10 text-blue-300 border border-blue-500/20',
    APPAREL: 'bg-teal-500/10 text-teal-300 border border-teal-500/20',
    SUBLIMATION: 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20',
    BUTTON: 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/20',
    EMBROIDERY: 'bg-rose-500/10 text-rose-300 border border-rose-500/20',
    UV_PRINTED: 'bg-cyan-500/10 text-cyan-300 border border-cyan-500/20',
    PRINT_ON_DEMAND: 'bg-gray-500/10 text-gray-300 border border-gray-500/20',
  };

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-sans text-3xl font-bold text-gray-900 dark:text-warm-cream flex items-center gap-3">
            <Factory className="w-8 h-8 text-amber-450" />
            Production
          </h1>
          <p className="text-gray-600 dark:text-warm-sand mt-1">
            Log production runs and track supply consumption
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-450/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-amber-450" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Total Variants</p>
              <p className="font-sans text-2xl font-bold text-gray-900 dark:text-warm-cream">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Ready to Produce</p>
              <p className="font-sans text-2xl font-bold text-green-600 dark:text-green-400">{stats.sufficient}</p>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Low / No Stock</p>
              <p className="font-sans text-2xl font-bold text-red-600 dark:text-red-400">{stats.insufficient}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 dark:text-warm-sand" />
          <input
            type="text"
            placeholder="Search products or variants…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input w-full pl-10"
          />
        </div>
        <div className="relative sm:w-56">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 dark:text-warm-sand" />
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="input w-full appearance-none pl-10 pr-10"
          >
            <option value="">All Locations</option>
            {locations?.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 dark:text-warm-sand pointer-events-none" />
        </div>
      </div>

      {/* Variant Table */}
      <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table w-full min-w-[700px]">
            <thead className="bg-gray-100 dark:bg-charcoal-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Product
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Variant
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Type
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Supplies Needed
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Units Makeable
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal-700/50">
              {productsLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-600 dark:text-warm-sand">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-450" />
                      <span>Loading variants…</span>
                    </div>
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-600 dark:text-warm-sand">
                    <div className="flex flex-col items-center gap-3">
                      <Factory className="w-10 h-10 opacity-30" />
                      <p>No variants found</p>
                      <p className="text-sm opacity-60">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRows.map(({ product, variant }) => {
                  const typeMeta = PRODUCT_TYPE_META[product.productType];
                  const badgeClass =
                    typeBadgeColors[product.productType] ||
                    'bg-gray-100 dark:bg-charcoal-700 text-gray-600 dark:text-warm-sand border border-gray-300 dark:border-charcoal-600';

                  return (
                    <tr
                      key={variant.id}
                      className="hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <EntityLink type="product" id={product.id} label={product.name} />
                        <p className="text-xs text-gray-600 dark:text-warm-sand mono">{product.sku}</p>
                      </td>

                      <td className="px-4 py-3">
                        <p className="text-gray-900 dark:text-warm-cream">{variant.displayName}</p>
                        <p className="text-xs text-gray-600 dark:text-warm-sand mono">{variant.variantSku}</p>
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}
                        >
                          <span>{typeMeta?.icon}</span>
                          <span>{typeMeta?.label || product.productType}</span>
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        {variant.variantFilaments?.length > 0 ? (
                          <div className="space-y-1">
                            {variant.variantFilaments.slice(0, 3).map((vf) => (
                              <div key={vf.id} className="flex items-center gap-2 text-xs">
                                <span
                                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                    vf.sufficientStock ? 'bg-green-400' : 'bg-red-400'
                                  }`}
                                />
                                <span className="text-gray-600 dark:text-warm-sand truncate max-w-[140px]">
                                  {vf.filament.name}
                                </span>
                                <span className="text-gray-600 dark:text-warm-sand/60 mono">
                                  {vf.quantityGrams}g
                                </span>
                              </div>
                            ))}
                            {variant.variantFilaments.length > 3 && (
                              <p className="text-xs text-gray-600 dark:text-warm-sand/50">
                                +{variant.variantFilaments.length - 3} more
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-600 dark:text-warm-sand/50 italic">None configured</span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${
                            variant.unitsMakeable > 0
                              ? 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'
                              : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                          }`}
                        >
                          <Hash className="w-3 h-3" />
                          {variant.unitsMakeable}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openModal(product, variant)}
                          className="flex items-center gap-1.5"
                        >
                          <Play className="w-3.5 h-3.5" />
                          Produce
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Produce Modal */}
      <ProduceModal
        state={modal}
        onClose={() => setModal({ isOpen: false, variant: null, product: null })}
        onSuccess={handleProduceSuccess}
      />
    </div>
  );
}
